import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import AppButton from "../../components/ui/AppButton";
import { formatNumericDate } from "../../utils/formatters";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const STATUS_META = {
  COMPLETED: { color: "success", icon: "fa-check-circle", key: "completed" },
  PENDING: { color: "warning", icon: "fa-clock", key: "pending" },
  REFUND_REQUESTED: { color: "info", icon: "fa-undo", key: "refundReq" },
  REFUNDED: { color: "secondary", icon: "fa-reply", key: "refunded" },
  REJECTED: { color: "danger", icon: "fa-times-circle", key: "rejected" },
  CANCELLED: { color: "dark", icon: "fa-ban", key: "cancelled" },
};

const UMFinanceTransactionTable = ({ transactions, loading, totalElements, clubs, events, filters, onFilterChange, onClearFilters }) => {
  const { t, i18n } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();
  const canFilterByClub = can(PERMISSIONS.club.list);
  const canFilterByEvent = can(PERMISSIONS.event.list);

  const getStatusBadge = (status) => {
    const meta = STATUS_META[status] || { color: "secondary", icon: "fa-question-circle", key: null };
    const label = meta.key ? t(`finance.ledger.${meta.key}`) : status;
    return (
      <span className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} fw-bold`}>
        <i className={`fas ${meta.icon} me-1`}></i>{label}
      </span>
    );
  };

  const getTypeLabel = (type) => (type === "EVENT_TICKET" ? t("finance.ledger.types.ticket") : t("finance.ledger.types.membership"));

  const selectedClub = clubs.find((c) => String(c.id) === String(filters.clubId));
  const selectedEvent = events.find((e) => String(e.id) === String(filters.eventId));
  const hasActiveFilters = Object.values(filters || {}).some((v) => v);

  return (
    <div className="um-finance-transaction-list">
      <ListToolbar
        title={t("finance.ledger.filters.title")}
        subtitle={t("finance.ledger.filters.subtitle", { count: totalElements })}
        filterBadges={hasActiveFilters ? (
          <>
            {filters.userEmail && <span className="badge bg-primary">{t("finance.ledger.filters.userEmail")}: {filters.userEmail}</span>}
            {filters.type && <span className="badge bg-secondary">{t("finance.ledger.filters.type")}: {getTypeLabel(filters.type)}</span>}
            {filters.clubId && <span className="badge bg-warning">{t("finance.ledger.filters.club")}: {selectedClub?.nameLocal || selectedClub?.nameEn}</span>}
            {filters.eventId && <span className="badge bg-success">{t("finance.ledger.filters.event")}: {selectedEvent?.name || selectedEvent?.titleLocal || selectedEvent?.titleEn}</span>}
            {filters.startDate && <span className="badge bg-info">{t("finance.ledger.filters.startDate")}: {formatNumericDate(filters.startDate, i18n.language)}</span>}
            {filters.endDate && <span className="badge bg-info">{t("finance.ledger.filters.endDate")}: {formatNumericDate(filters.endDate, i18n.language)}</span>}
          </>
        ) : null}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label fw-medium">{t("finance.ledger.filters.userEmail")}</label>
            <div className="input-group">
              <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
              <input
                type="text"
                className="form-control"
                placeholder={t("finance.ledger.filters.userEmailPlaceholder")}
                value={filters.userEmail || ""}
                onChange={(e) => onFilterChange("userEmail", e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-medium">{t("finance.ledger.filters.type")}</label>
            <select className="form-select" value={filters.type || ""} onChange={(e) => onFilterChange("type", e.target.value)}>
              <option value="">{t("finance.ledger.filters.allTypes")}</option>
              <option value="EVENT_TICKET">{t("finance.ledger.types.ticket")}</option>
              <option value="CLUB_MEMBERSHIP">{t("finance.ledger.types.membership")}</option>
            </select>
          </div>
          {canFilterByClub && (
            <div className="col-md-4">
              <label className="form-label fw-medium">{t("finance.ledger.filters.club")}</label>
              <select className="form-select" value={filters.clubId || ""} onChange={(e) => onFilterChange("clubId", e.target.value)}>
                <option value="">{t("finance.ledger.filters.allClubs")}</option>
                {clubs.map((club) => <option key={club.id} value={club.id}>{club.name || club.nameLocal || club.nameEn}</option>)}
              </select>
            </div>
          )}
          {canFilterByEvent && (
            <div className="col-md-4">
              <label className="form-label fw-medium">{t("finance.ledger.filters.event")}</label>
              <select className="form-select" value={filters.eventId || ""} onChange={(e) => onFilterChange("eventId", e.target.value)}>
                <option value="">{t("finance.ledger.filters.allEvents")}</option>
                {events.map((event) => <option key={event.id} value={event.id}>{event.name || event.titleLocal || event.titleEn}</option>)}
              </select>
            </div>
          )}
          <div className="col-md-4">
            <label className="form-label fw-medium">{t("finance.ledger.filters.startDate")}</label>
            <input type="date" className="form-control" value={filters.startDate || ""} onChange={(e) => onFilterChange("startDate", e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-medium">{t("finance.ledger.filters.endDate")}</label>
            <input type="date" className="form-control" value={filters.endDate || ""} onChange={(e) => onFilterChange("endDate", e.target.value)} />
          </div>
          {hasActiveFilters && (
            <div className="col-auto d-flex align-items-end">
              <AppButton variant="outline" className="btn-sm" onClick={onClearFilters}>
                <i className="fas fa-times me-1"></i>{tCommon("buttons.clearFilters")}
              </AppButton>
            </div>
          )}
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{tCommon("actions.loading")}</span></div>
              <p className="text-muted mt-2">{t("finance.ledger.filters.loading")}</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-receipt text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">{t("finance.ledger.filters.noFound")}</h5>
              <p className="text-muted">{hasActiveFilters ? t("finance.ledger.filters.adjustFilters") : t("finance.ledger.filters.noneCreated")}</p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("finance.ledger.columns.idRef")}</th>
                  <th>{t("finance.ledger.columns.user")}</th>
                  <th>{t("finance.ledger.columns.type")}</th>
                  <th className="text-center">{t("finance.ledger.columns.amount")}</th>
                  <th className="text-center">{t("finance.ledger.columns.status")}</th>
                  <th className="pe-4">{t("finance.ledger.columns.date")}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => (
                  <tr key={txn.transactionId}>
                    <td className="ps-4">
                      <div className="fw-bold" style={{ fontSize: "0.85rem" }}>{txn.transactionId}</div>
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>{txn.referenceCode}</div>
                    </td>
                    <td>
                      <div className="text-dark">{txn.userFullName}</div>
                      {txn.userEmail && <small className="text-muted">{txn.userEmail}</small>}
                    </td>
                    <td>
                      <span className="badge bg-secondary bg-opacity-10 text-secondary">{getTypeLabel(txn.transactionType)}</span>
                    </td>
                    <td className="text-center fw-bold">{Number(txn.amount).toLocaleString(i18n.language, { minimumFractionDigits: 2 })} {txn.currency}</td>
                    <td className="text-center">{getStatusBadge(txn.status)}</td>
                    <td className="pe-4 text-muted small">{formatNumericDate(txn.createdAt, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMFinanceTransactionTable;
